import React from 'react';
import { Button, Dropdown, Checkbox, Segmented } from 'antd';
import { SettingOutlined } from '@ant-design/icons';

export type Density = 'middle' | 'small';

export interface ToggleableColumn {
    key: string;
    label: string;
    /** Locked columns can't be hidden — identity and actions must always be reachable. */
    locked?: boolean;
}

interface TableToolbarProps {
    columns: ToggleableColumn[];
    /** key -> shown. A missing key counts as shown. */
    visible: Record<string, boolean>;
    onToggle: (key: string, next: boolean) => void;
    onReset: () => void;
    density: Density;
    onDensityChange: (d: Density) => void;
    /** Rendered on the left, e.g. a row count. */
    children?: React.ReactNode;
}

const TableToolbar: React.FC<TableToolbarProps> = ({
    columns,
    visible,
    onToggle,
    onReset,
    density,
    onDensityChange,
    children,
}) => {
    const hiddenCount = columns.filter((c) => !c.locked && visible[c.key] === false).length;

    const picker = (
        <div className="w-[210px] rounded-panel border border-line bg-white p-2 shadow-lifted">
            <div className="flex items-center justify-between px-2 pb-2 pt-1">
                <span className="text-[11px] font-semibold uppercase tracking-wider text-faint">列</span>
                <button
                    onClick={onReset}
                    className="text-[11px] text-brand transition-colors hover:text-brand-hover"
                >
                    重置
                </button>
            </div>
            <div className="max-h-[320px] overflow-y-auto">
                {columns.map((c) => (
                    <label
                        key={c.key}
                        className={[
                            'flex items-center gap-2 rounded-md px-2 py-1.5 text-[13px] transition-colors',
                            c.locked ? 'cursor-not-allowed opacity-45' : 'cursor-pointer hover:bg-canvas',
                        ].join(' ')}
                    >
                        <Checkbox
                            checked={c.locked ? true : visible[c.key] !== false}
                            disabled={c.locked}
                            onChange={(e) => onToggle(c.key, e.target.checked)}
                        />
                        <span className="truncate">{c.label}</span>
                    </label>
                ))}
            </div>
        </div>
    );

    return (
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
            <div className="text-[13px] text-muted">{children}</div>

            <div className="flex items-center gap-2">
                <Segmented
                    size="small"
                    value={density}
                    onChange={(v) => onDensityChange(v as Density)}
                    options={[
                        { label: '宽松', value: 'middle' },
                        { label: '紧凑', value: 'small' },
                    ]}
                />
                <Dropdown dropdownRender={() => picker} trigger={['click']} placement="bottomRight">
                    <Button size="small" icon={<SettingOutlined />}>
                        列{hiddenCount > 0 ? ` (${hiddenCount})` : ''}
                    </Button>
                </Dropdown>
            </div>
        </div>
    );
};

export default TableToolbar;
